/**
 * 单条消息组件
 */

import React, { useState, useCallback, useRef, useEffect } from 'react';
import { MarkdownContent } from '@/utils/markdown';
import { useChatStore } from '@/stores';
import type { Message } from '@/types';
import { ComposerModeSelect } from './ComposerModeSelect';
import { ansiToHtml } from '@/utils/ansiToHtml';

/** 聊天区宽度下大致每行字符数，用于单行 JSON（字面量 \\n）等仍按「视觉行数」折叠 */
const TOOL_OUTPUT_WRAP_CHARS = 52;
const COLLAPSE_VISUAL_LINES = 5;

function countToolOutputVisualLines(text: string): number {
  if (!text) return 0;
  const rows = text.split(/\r?\n/);
  let total = 0;
  for (const row of rows) {
    total += Math.max(1, Math.ceil(row.length / TOOL_OUTPUT_WRAP_CHARS));
  }
  return total;
}

/** Cursor 风格：点击正文进入编辑；底部工具栏在输入框内侧（Agent + 上箭头发送） */
function UserMessageComposer({
  message,
  onResend,
}: {
  message: Message;
  onResend?: (messageId: string, newContent: string) => void;
}) {
  const isProcessing = useChatStore((s) => s.isProcessing);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(message.content);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const composerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setDraft(message.content);
  }, [message.content, message.id]);

  useEffect(() => {
    if (!editing || !textareaRef.current) return;
    const el = textareaRef.current;
    el.focus();
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, 240) + 'px';
  }, [editing]);

  const canEdit = Boolean(onResend) && !isProcessing;

  const handleCancel = useCallback(() => {
    setDraft(message.content);
    setEditing(false);
  }, [message.content]);

  useEffect(() => {
    if (!editing) return;
    /** 用冒泡阶段，避免捕获先于子节点处理导致与「发送」等按钮点击冲突 */
    const onDocDown = (e: MouseEvent) => {
      const el = composerRef.current;
      const t = e.target as Node;
      if (el && !el.contains(t)) {
        handleCancel();
      }
    };
    document.addEventListener('mousedown', onDocDown, false);
    return () => document.removeEventListener('mousedown', onDocDown, false);
  }, [editing, handleCancel]);

  const handleBeginEdit = useCallback(() => {
    if (!onResend || isProcessing) return;
    setDraft(message.content);
    setEditing(true);
  }, [onResend, isProcessing, message.content]);

  const handleSubmit = useCallback(() => {
    const t = draft.trim();
    if (!t || isProcessing || !onResend) return;
    onResend(message.id, t);
    setEditing(false);
  }, [draft, isProcessing, message.id, onResend]);

  const handleDraftChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setDraft(e.target.value);
    const el = e.target;
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, 240) + 'px';
  }, []);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        handleCancel();
      }
      if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        handleSubmit();
      }
    },
    [handleCancel, handleSubmit]
  );

  return (
    <div
      ref={composerRef}
      className={`sse-cursor-user-composer${isProcessing ? ' is-processing' : ''}${!canEdit && !editing ? ' is-readonly' : ''}`}
    >
      {editing ? (
        <div className="sse-cursor-composer-edit-block">
          <textarea
            ref={textareaRef}
            className="sse-cursor-composer-input sse-cursor-composer-input--inset"
            value={draft}
            onChange={handleDraftChange}
            onKeyDown={handleKeyDown}
            aria-label="编辑并重新发送"
            rows={2}
          />
          <div className="sse-cursor-composer-inner-foot">
            <ComposerModeSelect compact menuUp disabled={isProcessing} />
            <button
              type="button"
              className="sse-cursor-composer-submit"
              title="发送"
              aria-label="发送"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleSubmit();
              }}
              disabled={!draft.trim() || isProcessing}
            >
              <span className="dashicons dashicons-arrow-up-alt2" aria-hidden />
            </button>
          </div>
        </div>
      ) : (
        <div
          className="sse-cursor-composer-body"
          role={onResend && canEdit ? 'button' : undefined}
          tabIndex={onResend && canEdit ? 0 : undefined}
          onClick={onResend && canEdit ? handleBeginEdit : undefined}
          onKeyDown={
            onResend && canEdit
              ? (e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleBeginEdit();
                  }
                }
              : undefined
          }
          title={
            !onResend
              ? undefined
              : isProcessing
                ? '处理结束后可编辑'
                : '点击编辑并重新发送（将删除本条之后的对话）'
          }
        >
          <MarkdownContent content={message.content} inlineRoot />
        </div>
      )}
    </div>
  );
}

interface MessageItemProps {
  message: Message;
  onUserMessageResend?: (messageId: string, newContent: string) => void;
}

export const MessageItem: React.FC<MessageItemProps> = ({ message, onUserMessageResend }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const isThinking = message.thinking !== undefined;
  const isResult = message.result !== undefined;
  const isUser = message.role === 'user';
  const isSystem = message.role === 'system';

  const handleToggle = useCallback(() => {
    setIsExpanded((prev) => !prev);
  }, []);

  // Cursor 风格：命令执行块（默认折叠，点击展开查看完整输出）
  if (message.commandBlock) {
    const cb = message.commandBlock;
    const statusIcon = cb.success ? '✓' : '✗';
    const statusText = cb.success
      ? `退出码 ${cb.exitCode ?? 0}`
      : `失败${cb.exitCode != null ? `（退出码 ${cb.exitCode}）` : ''}`;
    const outputHtml = ansiToHtml(cb.output || cb.error || '(无输出)');

    return (
      <div className="sse-ai-message assistant">
        <div className="message-content">
          <div
            className={`sse-cmd-block ${isExpanded ? 'is-expanded' : 'is-collapsed'} ${cb.success ? 'is-success' : 'is-failed'}`}
          >
            <button
              type="button"
              className="sse-cmd-block-header"
              onClick={handleToggle}
              aria-expanded={isExpanded}
            >
              <span className="sse-cmd-block-chevron">{isExpanded ? '▼' : '▶'}</span>
              <span className="sse-cmd-block-prompt">$</span>
              <span className="sse-cmd-block-cmd">{cb.command || '(未知命令)'}</span>
              <span className={`sse-cmd-block-status ${cb.success ? 'ok' : 'fail'}`}>
                {statusIcon} {statusText}
              </span>
            </button>
            {isExpanded && (
              <div className="sse-cmd-block-output-box">
                <pre
                  className="sse-cmd-block-output"
                  dangerouslySetInnerHTML={{ __html: outputHtml }}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (isThinking) {
    return (
      <div className="sse-ai-message assistant">
        <div className="message-content">
          <div
            className={`thinking-box ${isExpanded ? 'expanded' : ''}`}
            onClick={handleToggle}
          >
            <div className="content">{message.thinking}</div>
          </div>
        </div>
      </div>
    );
  }

  if (isResult) {
    const output = message.result?.output || message.result?.error || '(无输出)';
    return (
      <div className="sse-ai-message assistant">
        <div className="message-content">
          <div
            className={`result-box ${isExpanded ? 'expanded' : ''}`}
            onClick={handleToggle}
          >
            <div className="result-content">{output}</div>
          </div>
        </div>
      </div>
    );
  }

  if (isUser) {
    return (
      <div className="sse-ai-message user">
        <div className="message-content sse-user-message-shell">
          <UserMessageComposer message={message} onResend={onUserMessageResend} />
        </div>
      </div>
    );
  }

  if (isSystem) {
    return (
      <div className="sse-ai-message system">
        <div className="message-content">{message.content}</div>
      </div>
    );
  }

  const lines = message.content.split(/\r?\n/);
  const visualLineCount = countToolOutputVisualLines(message.content);
  const needsLongOutputCollapse =
    message.role === 'assistant' &&
    message.assistantKind === 'tool_output' &&
    visualLineCount > COLLAPSE_VISUAL_LINES;
  const previewLine = lines.find((l) => l.trim().length > 0) ?? lines[0] ?? '';

  if (needsLongOutputCollapse) {
    return (
      <div className="sse-ai-message assistant">
        <div
          className={`message-content sse-assistant-output-collapsible ${
            isExpanded ? 'is-expanded' : 'is-collapsed'
          }`}
          onClick={handleToggle}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              handleToggle();
            }
          }}
          role="button"
          tabIndex={0}
          aria-expanded={isExpanded}
          aria-label={
            isExpanded
              ? '工具输出已展开，点击收起'
              : `工具输出已折叠（约 ${visualLineCount} 行），点击展开`
          }
          title={
            isExpanded
              ? '点击收起'
              : `约 ${visualLineCount} 行（含长行折行），点击展开`
          }
        >
          {isExpanded ? (
            <MarkdownContent content={message.content} />
          ) : (
            <div className="sse-assistant-output-collapsed-strip">
              <div className="sse-assistant-output-preview">
                {previewLine || '（工具输出）'}
              </div>
              <span className="sse-assistant-output-collapse-hint">点击展开</span>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="sse-ai-message assistant">
      <div className="message-content">
        <MarkdownContent content={message.content} />
      </div>
    </div>
  );
};
